import { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Door,
  Mutation,
  MutationAssignToAccessGrantsArgs,
  MutationUnassignFromAccessGrantsArgs,
  MutationUpdateDoorArgs,
  Query,
  QueryGetVisitorGroupsForTenantArgs,
  UpdateDoorOfficeModeInput
} from 'graphql/generated/graphqlTypes';
import {
  ASSIGN_DEVICE_TO_ACCESS_GRANTS,
  ASSIGN_DOOR_TO_LOCATIONS,
  UNASSIGN_DEVICE_FROM_ACCESS_GRANTS,
  UPDATE_DOOR_INFO
} from 'graphql/v2/mutations';
import {
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_DOORS_FOR_TENANT,
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { useEffect, useMemo } from 'react';
import { getLockByLockId } from 'utils/Lock/Lock';
import {
  getAllAccessGrantsOfVisitorGroup,
  getVisitorGroupById,
  getVisitorGroupsThatHaveLockAssignedDirectly
} from 'utils/VisitorGroup/VisitorGroup';
import { stitchDoorLocations } from 'v2/utils/lock';
import { rebuildVisitorGroupsFromIds } from 'v2/utils/rebuildVisitorGroupsFromIds';
import { TranslatedHookProps } from '../hooks.types';

interface Props extends TranslatedHookProps {
  deviceId: string;
}

function diffIds(prev: string[], next: string[]) {
  const prevSet = new Set(prev);
  const nextSet = new Set(next);

  const added = next.filter((id) => !prevSet.has(id));
  const removed = prev.filter((id) => !nextSet.has(id));

  return { added, removed };
}

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_DOORS_FOR_TENANT_TYPED: TypedDocumentNode<Pick<Query, 'getDoorsForTenant'>> = GET_DOORS_FOR_TENANT;
const GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getLocationsForTenant'>> =
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS;
// Only ids are selected for the members and access grant locations/doors, so the `memberIds`, `locationIds` and
// `doorIds` of these groups are turned back into the `members`, `locations` and `doors` the helpers read.
const GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>,
  QueryGetVisitorGroupsForTenantArgs
> = GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS;
const UPDATE_DOOR_INFO_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateDoor'>,
  MutationUpdateDoorArgs
> = UPDATE_DOOR_INFO;
const ASSIGN_DOOR_TO_LOCATIONS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateDoor'>,
  MutationUpdateDoorArgs
> = ASSIGN_DOOR_TO_LOCATIONS;
const ASSIGN_DEVICE_TO_ACCESS_GRANTS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'assignToAccessGrants'>,
  MutationAssignToAccessGrantsArgs
> = ASSIGN_DEVICE_TO_ACCESS_GRANTS;
const UNASSIGN_DEVICE_FROM_ACCESS_GRANTS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'unassignFromAccessGrants'>,
  MutationUnassignFromAccessGrantsArgs
> = UNASSIGN_DEVICE_FROM_ACCESS_GRANTS;

const useDeviceDetails = ({ translation, handleFetchError, deviceId }: Props) => {
  // Apollo 4 notifies on network status changes by default, which flips `loading` to true on every refetch or
  // poll. Keep it off so a refetch does not blank content that is already on screen.
  const {
    loading: doorsLoading,
    error: doorsError,
    data: doors,
    refetch: refetchDoors
  } = useQuery(GET_DOORS_FOR_TENANT_TYPED, { notifyOnNetworkStatusChange: false });

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  const {
    loading: locationsLoading,
    error: locationsError,
    data: locations,
    refetch: refetchLocations
  } = useQuery(GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED, { notifyOnNetworkStatusChange: false });

  useEffect(() => {
    if (locationsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(locationsError);
    }
  }, [locationsError]);

  const {
    loading: visitorGroupsLoading,
    error: visitorGroupsError,
    data: visitorGroupsData
  } = useQuery(GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED, { notifyOnNetworkStatusChange: false });

  // Only ids are read from the members, locations and doors, and the locations also carry the ids of their doors.
  const visitorGroups = useMemo(
    () =>
      visitorGroupsData
        ? rebuildVisitorGroupsFromIds(
            visitorGroupsData.getVisitorGroupsForTenant,
            locations?.getLocationsForTenant ?? []
          )
        : undefined,
    [visitorGroupsData, locations]
  );

  useEffect(() => {
    if (visitorGroupsError) {
      handleFetchError(translation.devices_error_assigning);
      console.error(visitorGroupsError);
    }
  }, [visitorGroupsError]);

  // The object form of the refetch always fetches, whereas a bare document only refetches the queries that are active.
  const [updateDoorInfoMutation, { loading: updateDoorInfoLoading }] = useMutation(UPDATE_DOOR_INFO_TYPED, {
    refetchQueries: (result) => [
      { query: GET_DOORS_FOR_TENANT, variables: { filter: { id: result.data?.updateDoor?.id } } },
      { query: GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS }
    ],
    awaitRefetchQueries: true
  });

  const updateDoorInfo = async (
    name: string,
    doorId: string,
    officeModeInput: UpdateDoorOfficeModeInput | undefined
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    try {
      const result = await updateDoorInfoMutation({
        variables: { doorId, name, officeMode: officeModeInput }
      });
      const doorInfo = result.data?.updateDoor?.id;
      if (!doorInfo) return { success: false, errorMessage: translation.error_while_updating_device };
      return { success: true };
    } catch (err) {
      return { success: false, errorMessage: err instanceof Error ? err.message : translation.an_error_occurred };
    }
  };

  const [assignDoorToLocationsMutation, { loading: assignDoorToLocationsLoading }] = useMutation(
    ASSIGN_DOOR_TO_LOCATIONS_TYPED,
    {
      refetchQueries: (result) => [
        { query: GET_DOORS_FOR_TENANT, variables: { filter: { id: result.data?.updateDoor?.id } } },
        { query: GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS }
      ],
      awaitRefetchQueries: true
    }
  );

  const [
    assignDoorToAccessGrantMutation,
    { loading: assignDoorToAccessGrantLoading, error: assignDoorToAccessGrantError }
  ] = useMutation(ASSIGN_DEVICE_TO_ACCESS_GRANTS_TYPED, {
    // The mutations return the access grant's `doors`, not the `doorIds` of the slim groups, so those are refetched.
    refetchQueries: [GET_DOORS_FOR_TENANT, { query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    if (assignDoorToAccessGrantError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(assignDoorToAccessGrantError);
    }
  }, [assignDoorToAccessGrantError]);

  const [
    unassignDoorFromAccessGrantMutation,
    { loading: unassignDoorFromAccessGrantLoading, error: unassignDoorFromAccessGrantError }
  ] = useMutation(UNASSIGN_DEVICE_FROM_ACCESS_GRANTS_TYPED, {
    // The mutations return the access grant's `doors`, not the `doorIds` of the slim groups, so those are refetched.
    refetchQueries: [GET_DOORS_FOR_TENANT, { query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    if (unassignDoorFromAccessGrantError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(unassignDoorFromAccessGrantError);
    }
  }, [unassignDoorFromAccessGrantError]);

  const assignDoorToDoorGroupOrVisitorGroup = async (
    door: Door,
    doorGroupIds: string[],
    visitorGroupIds: string[]
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    // Without the groups the current assignments of the device are unknown, so nothing is changed.
    if (!visitorGroups) return { success: false, errorMessage: translation.devices_error_assigning };

    try {
      const result = await assignDoorToLocationsMutation({
        variables: { doorId: door.id, name: door.name, locationIds: doorGroupIds }
      });
      const doorId = result.data?.updateDoor?.id;
      if (!doorId) return { success: false, errorMessage: translation.error_while_updating_device };
      const accessGrantsIdsBeforeAssigning = getVisitorGroupsThatHaveLockAssignedDirectly(
        visitorGroups,
        deviceId
      ).flatMap((visitorGroup) =>
        getAllAccessGrantsOfVisitorGroup(visitorGroup).flatMap((accessGrant) => accessGrant.id)
      );
      const visitorGroupsToAssign = visitorGroupIds
        .map((visitorGroupId) => getVisitorGroupById(visitorGroups, visitorGroupId))
        .filter((visitorGroup) => visitorGroup !== undefined);

      const accessGrantsIdsToAssign = visitorGroupsToAssign.flatMap((visitorGroup) =>
        getAllAccessGrantsOfVisitorGroup(visitorGroup).flatMap((accessGrant) => accessGrant.id)
      );
      const { added, removed } = diffIds(accessGrantsIdsBeforeAssigning, accessGrantsIdsToAssign);
      if (added.length > 0)
        await assignDoorToAccessGrantMutation({
          variables: { doorIds: [doorId], accessGrantIds: added, locationIds: [] }
        });
      if (removed.length > 0)
        await unassignDoorFromAccessGrantMutation({
          variables: { doorIds: [doorId], accessGrantIds: removed, locationIds: [] }
        });
      return { success: true };
    } catch (err) {
      return { success: false, errorMessage: err instanceof Error ? err.message : translation.an_error_occurred };
    }
  };

  const stitchedDoors = useMemo(
    () => stitchDoorLocations(doors?.getDoorsForTenant ?? [], locations?.getLocationsForTenant ?? []),
    [doors, locations]
  );

  return {
    device: getLockByLockId(stitchedDoors, deviceId),
    // The device is stitched from the doors and the locations, so it is only complete once both have loaded.
    deviceLoading: doorsLoading || locationsLoading,
    hasDeviceFetchError: Boolean(doorsError || locationsError),
    // Failed retries are already reported through `doorsError` and `locationsError`, so the rejections are only
    // swallowed here. Both requests still start together, and the returned promise never rejects.
    retryFetchingDevice: () => Promise.allSettled([refetchDoors(), refetchLocations()]),
    locations: locations?.getLocationsForTenant,
    locationsLoading,
    visitorGroups,
    visitorGroupsLoading,
    updateDoorInfo,
    updateDoorInfoLoading,
    assignDoorToDoorGroupOrVisitorGroup,
    assignDoorToDoorGroupOrVisitorGroupLoading:
      assignDoorToLocationsLoading || assignDoorToAccessGrantLoading || unassignDoorFromAccessGrantLoading
  };
};

export default useDeviceDetails;
